import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X, Check, Eye, EyeOff, Wrench, Droplets, Settings, Zap, Wind, CircleDot, Shield, Star } from 'lucide-react';
import TopBar from '../components/layout/TopBar';
import './ServicesManagePage.css';

const ICON_OPTIONS = ['Wrench','Settings','Droplets','Zap','Wind','CircleDot','Shield','Star'];
const ICON_MAP = { Wrench:<Wrench size={20}/>, Settings:<Settings size={20}/>, Droplets:<Droplets size={20}/>, Zap:<Zap size={20}/>, Wind:<Wind size={20}/>, CircleDot:<CircleDot size={20}/>, Shield:<Shield size={20}/>, Star:<Star size={20}/> };
const CATEGORIES = ['Mechanical','Wash & Detail','Electrical','Tyres & Wheels'];

const INITIAL_SERVICES = [
  { id:1, name:'Full Service Package',      category:'Mechanical',     price:4999, duration:'6–8 hrs', icon:'Wrench',      active:true,  tag:'Most Popular', desc:'Comprehensive bumper-to-bumper inspection and service.', includes:'Engine oil & filter,Air & cabin filter,Brake inspection,50-point safety check' },
  { id:2, name:'Engine Oil Change',         category:'Mechanical',     price:999,  duration:'45 min',  icon:'Settings',    active:true,  tag:'',             desc:'Premium synthetic or semi-synthetic oil change.',       includes:'Synthetic/semi-synthetic oil,OEM filter,Oil level check' },
  { id:3, name:'Premium Detail Wash',       category:'Wash & Detail',  price:1499, duration:'3–4 hrs', icon:'Droplets',    active:true,  tag:'Top Rated',    desc:'Hand-crafted exterior wash, clay bar & interior clean.', includes:'Hand wash & dry,Clay bar,Interior vacuum,Tyre dressing' },
  { id:4, name:'Ceramic Coating',           category:'Wash & Detail',  price:12999,duration:'1–2 days',icon:'Shield',      active:true,  tag:'Premium',      desc:'9H ceramic paint protection for long-lasting gloss.',   includes:'Paint decontamination,Machine polishing,9H ceramic coating,5-year warranty' },
  { id:5, name:'Electrical Diagnostics',   category:'Electrical',     price:799,  duration:'1–2 hrs', icon:'Zap',         active:true,  tag:'',             desc:'Advanced OBD-II scan and electrical system inspection.', includes:'OBD-II full scan,Battery load test,Alternator check' },
  { id:6, name:'AC Service & Gas Refill',  category:'Mechanical',     price:1799, duration:'2–3 hrs', icon:'Wind',        active:true,  tag:'',             desc:'Complete AC service including gas top-up.',            includes:'Refrigerant top-up,Condenser clean,Cabin filter' },
  { id:7, name:'Wheel Alignment & Balance',category:'Tyres & Wheels', price:1299, duration:'1.5 hrs', icon:'CircleDot',   active:true,  tag:'',             desc:'4-wheel computerised alignment with dynamic balancing.', includes:'3D wheel alignment,Dynamic balancing,Tyre rotation' },
  { id:8, name:'Tyre Replacement',         category:'Tyres & Wheels', price:2500, duration:'1–2 hrs', icon:'CircleDot',   active:true,  tag:'',             desc:'Wide selection of brand tyres with expert fitting.',    includes:'Brand tyre options,Expert mounting,Wheel balancing' },
];

const BLANK = { name:'', category:'Mechanical', price:'', duration:'', icon:'Wrench', active:true, tag:'', desc:'', includes:'' };

export default function ServicesManagePage() {
  const [services, setServices]   = useState(INITIAL_SERVICES);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [form, setForm]           = useState(BLANK);
  const [filterCat, setFilterCat] = useState('All');

  const upd = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const openAdd  = () => { setEditItem(null); setForm(BLANK); setShowModal(true); };
  const openEdit = (s) => { setEditItem(s); setForm({ ...s, price: s.price, includes: Array.isArray(s.includes) ? s.includes.join(',') : s.includes }); setShowModal(true); };

  const toggleActive = (id) => setServices(p => p.map(s => s.id === id ? { ...s, active: !s.active } : s));
  const deleteService = (id) => setServices(p => p.filter(s => s.id !== id));

  const save = () => {
    if (!form.name || !form.price) return;
    if (editItem) {
      setServices(p => p.map(s => s.id === editItem.id ? { ...s, ...form, price: Number(form.price) } : s));
    } else {
      setServices(p => [...p, { ...form, id: Date.now(), price: Number(form.price) }]);
    }
    setShowModal(false);
  };

  const filtered = filterCat === 'All' ? services : services.filter(s => s.category === filterCat);
  const activeCount = services.filter(s => s.active).length;

  return (
    <>
      <TopBar title="Services" subtitle="Manage services shown to customers" />
      <div className="admin-content">
        <div className="page-hdr">
          <div className="page-hdr__left">
            <h1>Service Catalogue</h1>
            <p>{activeCount} active · {services.length - activeCount} hidden · {services.length} total</p>
          </div>
          <button className="btn btn-primary" onClick={openAdd}><Plus size={15} /> Add Service</button>
        </div>

        {/* Category filter */}
        <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
          {['All', ...CATEGORIES].map(c => (
            <button key={c} className={`filter-chip ${filterCat===c?'active':''}`} onClick={() => setFilterCat(c)}>{c}</button>
          ))}
        </div>

        {/* Services grid */}
        <div className="svcm-grid stagger">
          {filtered.map(s => (
            <div key={s.id} className={`svcm-card card afu ${!s.active ? 'svcm-card--hidden' : ''}`}>
              <div className="svcm-card__top">
                <div className="svcm-card__icon">{ICON_MAP[s.icon] || <Wrench size={20}/>}</div>
                <div className="svcm-card__meta">
                  {s.tag && <span className="svcm-tag">{s.tag}</span>}
                  <span className={`badge ${s.active ? 'badge-success' : 'badge-neutral'}`}>
                    {s.active ? 'Visible' : 'Hidden'}
                  </span>
                </div>
              </div>
              <h3 className="svcm-card__name">{s.name}</h3>
              <p className="svcm-card__category">{s.category}</p>
              <p className="svcm-card__desc">{s.desc}</p>
              <div className="svcm-card__includes">
                {(Array.isArray(s.includes) ? s.includes : s.includes.split(',')).slice(0,3).map(i => (
                  <span key={i} className="svcm-include-tag">{i.trim()}</span>
                ))}
              </div>
              <div className="svcm-card__footer">
                <div>
                  <span className="svcm-card__price">₹{Number(s.price).toLocaleString('en-IN')}</span>
                  <span className="svcm-card__duration"> · {s.duration}</span>
                </div>
                <div className="svcm-card__actions">
                  <button className="tbl-btn" onClick={() => toggleActive(s.id)} title={s.active ? 'Hide from customers' : 'Show to customers'}>
                    {s.active ? <EyeOff size={13}/> : <Eye size={13}/>}
                  </button>
                  <button className="tbl-btn" onClick={() => openEdit(s)} title="Edit"><Edit2 size={13}/></button>
                  <button className="tbl-btn tbl-btn--danger" onClick={() => deleteService(s.id)} title="Delete"><Trash2 size={13}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" style={{ maxWidth:600 }} onClick={e => e.stopPropagation()}>
              <div className="modal__header">
                <h3 className="modal__title">{editItem ? 'Edit Service' : 'Add New Service'}</h3>
                <button className="modal__close" onClick={() => setShowModal(false)}><X size={18}/></button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label className="label">Service Name *</label>
                    <input className="input" placeholder="e.g. Full Service Package" value={form.name} onChange={e => upd('name', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Category *</label>
                    <select className="input" value={form.category} onChange={e => upd('category', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Icon</label>
                    <select className="input" value={form.icon} onChange={e => upd('icon', e.target.value)}>
                      {ICON_OPTIONS.map(i => <option key={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Price (₹) *</label>
                    <input className="input" type="number" placeholder="e.g. 4999" value={form.price} onChange={e => upd('price', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Duration</label>
                    <input className="input" placeholder="e.g. 2–3 hrs" value={form.duration} onChange={e => upd('duration', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Tag (optional)</label>
                    <input className="input" placeholder="e.g. Most Popular" value={form.tag} onChange={e => upd('tag', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Visible to Customers</label>
                    <div style={{ display:'flex', gap:10, marginTop:6 }}>
                      {[true, false].map(v => (
                        <button key={String(v)} type="button"
                          className={`filter-chip ${form.active === v ? 'active' : ''}`}
                          onClick={() => upd('active', v)}>
                          {v ? 'Yes – Show' : 'No – Hide'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label className="label">Description</label>
                    <textarea className="input" rows={2} placeholder="Brief description of this service" style={{ resize:'vertical' }} value={form.desc} onChange={e => upd('desc', e.target.value)} />
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label className="label">Includes (comma-separated)</label>
                    <input className="input" placeholder="Engine oil & filter, Air filter, Brake inspection" value={form.includes} onChange={e => upd('includes', e.target.value)} />
                    <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>These appear as checklist items on the service card in the customer app.</p>
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={save}><Check size={14}/> {editItem ? 'Save Changes' : 'Add Service'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}